import React, { useState } from 'react';
import { Box, Button, Typography, Slide, Paper, useTheme, useMediaQuery } from '@mui/material';
import { Trans } from '@lingui/react/macro';
import { useAnalytics } from '../contexts/AnalyticsContext';
import { RetroTheme } from '../types/theme';

/**
 * Cookie consent banner component
 * Displays a banner at the bottom of the screen to get user consent for analytics cookies
 */
const CookieConsent: React.FC = () => {
  const theme = useTheme() as RetroTheme;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { setConsent, showConsentBanner, setShowConsentBanner } = useAnalytics();
  
  // Local state to control the animation
  const [visible, setVisible] = useState(showConsentBanner);

  // Handle accept button click
  const handleAccept = () => {
    setConsent(true);
    setVisible(false);
    setTimeout(() => setShowConsentBanner(false), 300); // Wait for animation to complete
  };

  // Handle decline button click
  const handleDecline = () => {
    setConsent(false);
    setVisible(false);
    setTimeout(() => setShowConsentBanner(false), 300); // Wait for animation to complete
  };

  // Don't render if consent has already been given or declined
  if (!showConsentBanner) {
    return null;
  }

  return (
    <Slide direction="up" in={visible} mountOnEnter unmountOnExit>
      <Paper
        elevation={3}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          borderTop: `1px solid ${theme.palette.primary.main}`,
          boxShadow: `0 0 5px ${theme.custom.glowPrimary}`,
        }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'stretch' : 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography variant="body2" color="primary.main">
            <Trans id="cookies.consent.message">
              This website uses cookies to enhance your experience and analyze site usage. 
              We use Google Analytics to collect anonymous information about how you use our site.
              You can choose to accept or decline these cookies.
            </Trans>
          </Typography>
          
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexShrink: 0,
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              onClick={handleDecline}
              sx={{
                borderRadius: 0,
                border: `1px solid ${theme.palette.primary.main}`,
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: 'rgba(0, 204, 204, 0.1)',
                  boxShadow: `0 0 5px ${theme.custom.glowPrimary}`,
                },
              }}
            >
              <Trans id="cookies.consent.decline">Decline</Trans>
            </Button>
            
            <Button
              variant="contained"
              color="primary"
              onClick={handleAccept}
              sx={{
                borderRadius: 0,
                backgroundColor: 'rgba(0, 204, 204, 0.2)',
                boxShadow: `0 0 8px ${theme.custom.glowPrimary}`,
                '&:hover': {
                  backgroundColor: 'rgba(0, 204, 204, 0.3)',
                  boxShadow: `0 0 10px ${theme.custom.glowPrimary}`,
                },
                color: '#fff',
              }}
            >
              <Trans id="cookies.consent.accept">Accept</Trans>
            </Button>
          </Box>
        </Box>
      </Paper>
    </Slide>
  );
};

export default CookieConsent;
